import CMSClient from "./rest/client";
import { cmsUrls, getUrl } from "./urls";
import I18n from "./i18n";
import Tracking from "./tracking";
import Newsletter from "./newsletter";
import AccountHandler from "./account";
import { type ServicePlayer, playerFromService } from "./player";
import { parseUmgTags } from "./metatags";
import type { CampaignFull } from "./rest/types";
import Campaign from "./campaign";
import TokenGate from "./token_gate";
import type { MediaPickerParams } from "./types";

interface Options {
  allowedOrigins?: string[];
  allowLocalhost?: boolean;
  defaultApiHost?: string;
  cmsEnvironment?: "staging" | "production" | "local";
}

interface MessageOut {
  type: string;
}

const MSG_READY_OUT = "ready";
const MSG_READY_IN = "ready:v2";
const MSG_CAMPAIGN_INFO = "campaignInfo:v2";
const MSG_CAMPAIGN_SAVED = "campaignSaved";
const MSG_TOKEN = "token";
const MSG_CAMPAIGN_URL = "campaignUrl";
const MSG_MEDIA_PICKED = "mediaPicked";
const MSG_MEDIA_OPEN = "openMediaLibrary:v2";
const MSG_THEME = "theme";

interface ReadyMessageOut extends MessageOut {
  type: typeof MSG_READY_OUT;
}

interface CampaignSavedMessageOut extends MessageOut {
  type: typeof MSG_CAMPAIGN_SAVED;
  campaignId: string | number;
}

interface MediaLibraryMessageOut extends MessageOut {
  type: typeof MSG_MEDIA_OPEN;
  fieldId?: string | null;
  currentFileUrl: string | null;
  targetWidth?: number | null;
  targetHeight?: number | null;
}

type AnyMessageOut =
  | ReadyMessageOut
  | CampaignSavedMessageOut
  | MediaLibraryMessageOut;

type TokenCallback = (token: string) => void;
type ThemeCallback = (theme: string) => void;
type CampaignUrlCallback = TokenCallback;
type MediaPickedCallback = (p: { url: string; currentFileId: string }) => void;
type AnyCallback = TokenCallback | CampaignUrlCallback | MediaPickedCallback;

interface MessageCallbacks extends Record<string, AnyCallback | undefined> {
  token?: TokenCallback;
  theme?: ThemeCallback;
  campaignUrl?: CampaignUrlCallback;
  mediaPicked?: MediaPickedCallback;
}

interface MessageIn {
  type: string;
}

interface ReadyMessageIn extends MessageIn {
  type: typeof MSG_READY_IN;
  campaign?: CampaignFull;
}

interface CampaignInfoMessageIn extends MessageIn {
  type: typeof MSG_CAMPAIGN_INFO;
  campaign: CampaignFull;
}

interface TokenMessageIn extends MessageIn {
  type: typeof MSG_TOKEN;
  token: string;
}

interface ThemeMessageIn extends MessageIn {
  type: typeof MSG_THEME;
  theme: string;
}

interface CampaignUrlMessageIn extends MessageIn {
  type: typeof MSG_CAMPAIGN_URL;
  url: string;
}

interface MediaPickedMessageIn extends MessageIn {
  type: typeof MSG_MEDIA_PICKED;
  url: string;
  // currentFileId: string;
  fieldId: string | null;
}

type AnyMessageIn =
  | TokenMessageIn
  | ThemeMessageIn
  | CampaignUrlMessageIn
  | MediaPickedMessageIn
  | ReadyMessageIn
  | CampaignInfoMessageIn;

interface MessageQueue extends Record<string, any> {
  campaignUrl: string[];
  mediaPicked: { url: string; fieldId: string }[];
  theme: string[];
}

declare global {
  interface Window {
    _umg_cms: CMSConnector;
  }
}

export default class CMSConnector {
  private allowedOrigins = [cmsUrls.live, cmsUrls.staging, cmsUrls.local];

  private allowLocalhost = true;
  private currentWindow: Window;
  private parentWindow?: Window;
  public parentOrigin?: string;
  private messageCallbacks: MessageCallbacks;
  private messageQueue: MessageQueue;
  public accessToken: string | null = null;
  private apiClient: CMSClient | null = null;
  private defaultApiHost: string;
  public campaign!: Campaign;
  public tracking: Tracking;
  public newsletter: Newsletter;
  public cmsEnvironment: "staging" | "production" | "local" = "staging";
  private tokenResolvers: any[] = [];
  private readyResolvers: any[] = [];
  private mediaResolvers: { [fileId: string]: any } = {};
  private iframeDetected = false;
  private iframeEnabled = true;
  private i18nInstance: I18n | null = null;
  private tokenGateInstance: TokenGate | null = null;
  private accountInstance: AccountHandler | null = null;
  private playerInstance: ServicePlayer | null = null;

  constructor(options: Options = {}) {
    this.handleMessage = this.handleMessage.bind(this);
    this.tracking = new Tracking(this);
    this.newsletter = new Newsletter(this);

    if (options.allowLocalhost !== undefined) {
      this.allowLocalhost = options.allowLocalhost;
    }

    this.cmsEnvironment = options.cmsEnvironment || this.cmsEnvironment;

    this.allowedOrigins = options.allowedOrigins || this.allowedOrigins;
    this.messageCallbacks = {};
    this.messageQueue = {
      token: [],
      campaignUrl: [],
      mediaPicked: [],
      theme: [],
    };

    this.currentWindow = window;
    this.defaultApiHost = getUrl(options.defaultApiHost || this.cmsEnvironment);

    if (this.currentWindow.parent !== this.currentWindow) {
      this.inIframe = true;
      this.parentWindow = this.currentWindow.parent;
    }
    this.setParentFromOpenerMaybe();
  }

  async init({
    iframeEnabled = true,
    i18n = true,
    account = true,
    tokenGate = true,
    tracking = true,
  } = {}): Promise<boolean> {
    window._umg_cms = this;
    this.iframeEnabled = iframeEnabled;
    this.tearDown();
    this.loadCampaignInfo();
    if (this.inIframe) {
      this.addMessageHandler();
      this.sendMessage({ type: MSG_READY_OUT }, true);
      await this.ready();
      await this.token();
    }
    if (account) {
      console.debug("CMS init account");
      await this.account.init();
    }
    if (i18n) {
      console.debug("CMS init i18n");
      await this.i18n.load();
    }
    if (tracking) {
      this.tracking.init(); // We want account and i18n available before tracking page_meta
    }
    if (tokenGate) {
      console.debug("CMS init tokenGate");
      await this.tokenGate.init();
    }
    console.debug("CMS init completed");
    return this.inIframe;
  }

  /** Get the music player for the current user's streaming service.
      The instance is cached and same instance is returned on subsequent calls.
      Throws an error if user is not logged in. */
  getPlayer() {
    if (this.playerInstance) {
      return this.playerInstance;
    }
    if (!this.account.currentAccount) {
      throw new Error("User account must be logged in to load player");
    }
    this.playerInstance = playerFromService(
      this.account.currentAccount.service,
      this
    );
    return this.playerInstance;
  }

  get player(): ServicePlayer {
    console.warn("cms.player is deprecated, use cms.getPlayer() instead");
    return this.getPlayer();
  }

  private get inIframe(): boolean {
    return this.iframeEnabled && this.iframeDetected;
  }

  private set inIframe(v: boolean) {
    this.iframeDetected = v;
  }

  private setParentFromOpenerMaybe(): void {
    if (!this.currentWindow.opener) {
      return;
    }
    if (this.currentWindow.opener === this.currentWindow) {
      return;
    }
    const referrer = this.currentWindow.document.referrer;
    if (!referrer) {
      return;
    }
    const origin = new URL(referrer).origin;
    if (!this.checkIncomingOrigin(origin)) {
      return;
    }
    this.parentWindow = this.currentWindow.opener;
    this.inIframe = true;
  }

  private async ready(): Promise<void> {
    if (this.parentOrigin || !this.inIframe) {
      return;
    }
    const promise: Promise<void> = new Promise((resolve) => {
      this.readyResolvers.push(resolve);
    });
    return await promise;
  }

  get api(): CMSClient {
    if (this.apiClient) {
      return this.apiClient;
    }
    this.apiClient = new CMSClient(
      () => this.parentOrigin || this.defaultApiHost,
      () => this.accessToken || ""
    );
    return this.apiClient;
  }

  get i18n(): I18n {
    if (this.i18nInstance) {
      return this.i18nInstance;
    }
    this.i18nInstance = new I18n(this);
    return this.i18nInstance;
  }

  get tokenGate(): TokenGate {
    if (this.tokenGateInstance) {
      return this.tokenGateInstance;
    }
    this.tokenGateInstance = new TokenGate(this);
    return this.tokenGateInstance;
  }

  get account(): AccountHandler {
    if (this.accountInstance) {
      return this.accountInstance;
    }
    this.accountInstance = new AccountHandler(this);
    return this.accountInstance;
  }

  private loadCampaignInfo(): void {
    const result = parseUmgTags();
    this.campaign = new Campaign(result.campaign);
    this.defaultApiHost = result.defaultApiHost;
    this.cmsEnvironment = result.cmsEnvironment;
  }

  tearDown(): void {
    this.removeMessageHandler();
  }

  onTheme(callback: ThemeCallback): void {
    this.messageCallbacks.theme = callback;
    this.pushFromQueue(MSG_THEME);
  }

  onCampaignUrl(callback: CampaignUrlCallback): void {
    // TODO: Remove this, campaign info is better
    this.messageCallbacks.campaignUrl = callback;
    this.pushFromQueue(MSG_CAMPAIGN_URL);
  }

  onMediaPicked(callback: MediaPickedCallback): void {
    this.messageCallbacks.mediaPicked = callback;
    this.pushFromQueue(MSG_MEDIA_PICKED);
  }

  onToken(callback: TokenCallback): void {
    this.messageCallbacks.token = callback;
    this.pushFromQueue(MSG_TOKEN);
  }

  campaignSaved(campaignId: string | number): void {
    this.sendMessage({ type: MSG_CAMPAIGN_SAVED, campaignId: campaignId });
    if (!this.campaign) {
      throw new Error(
        "CMSConnector.campaign instance has not been set. Please call `await cms.init(...) first`"
      );
    }
    this.campaign.app_id = campaignId.toString();
  }

  openMediaLibrary(
    params?: string | MediaPickerParams,
    currentFileUrl?: string | null
  ): MediaLibraryMessageOut {
    let fieldId: string | null;
    let msgParams: MediaPickerParams | null;
    if (typeof params === "string") {
      fieldId = params;
      msgParams = null;
    } else {
      msgParams = params || null;
      fieldId = params?.fieldId || null;
    }
    const msg: MediaLibraryMessageOut = {
      type: MSG_MEDIA_OPEN,
      fieldId: fieldId,
      currentFileUrl: currentFileUrl || msgParams?.currentFileUrl || null,
      targetWidth: msgParams?.targetWidth || null,
      targetHeight: msgParams?.targetHeight || null,
    };
    this.sendMessage(msg);
    return msg;
  }

  async selectMediaFile(
    params?: string | MediaPickerParams,
    currentFileUrl?: string | null // Legacy, part of params now
  ): Promise<MediaPickedMessageIn> {
    const msgOut = this.openMediaLibrary(params, currentFileUrl);
    const thePromise: Promise<MediaPickedMessageIn> = new Promise((resolve) => {
      this.mediaResolvers[msgOut.fieldId || ""] = (data: any) => resolve(data);
    });
    return await thePromise;
  }

  /**
   * Promise that returns access token when available.
   */
  private async token(): Promise<string> {
    if (!this.inIframe) {
      return "";
    }
    if (this.accessToken) {
      return this.accessToken;
    }
    const thePromise: Promise<string> = new Promise((resolve) => {
      this.tokenResolvers.push((token: string) => resolve(token));
    });
    return await thePromise;
  }

  private pushFromQueue(q: string): void {
    const cb = this.messageCallbacks[q];
    if (!cb) {
      return;
    }
    const queue = this.messageQueue[q];
    while (queue.length > 0) {
      const msg = queue.shift();
      if (!msg) {
        continue;
      }
      cb(msg);
    }
  }

  private sendMessage(msg: AnyMessageOut, anyOrigin = false): void {
    if (!this.parentWindow) {
      return;
    }
    const origin = anyOrigin ? "*" : this.parentOrigin;
    if (!origin) {
      return;
    }
    this.parentWindow.postMessage(msg, origin);
  }

  private addMessageHandler(): void {
    this.currentWindow.addEventListener("message", this.handleMessage);
  }

  private removeMessageHandler(): void {
    this.currentWindow.removeEventListener("message", this.handleMessage);
  }

  private checkIncomingOrigin(origin: string): boolean {
    if (this.parentOrigin) {
      return origin === this.parentOrigin;
    } else if (this.allowedOrigins.includes(origin)) {
      return true;
    } else if (
      this.allowLocalhost &&
      this.currentWindow.location.hostname === "localhost" &&
      new URL(origin).hostname === "localhost"
    ) {
      return true;
    }
    return false;
  }

  private handleMessage(event: MessageEvent): void {
    if (!this.checkIncomingOrigin(event.origin)) {
      // ignore message from unknown origin
      return;
    }
    const data: AnyMessageIn = event.data;
    const msgType = data.type;

    if (data.type === MSG_READY_IN && !this.parentOrigin) {
      if (data.campaign) {
        this.campaign.update(data.campaign);
      }
      this.parentOrigin = event.origin;
      while (this.readyResolvers.length > 0) {
        this.readyResolvers.pop()();
      }
      return;
    }

    switch (msgType) {
      case MSG_TOKEN: {
        this.messageQueue[msgType].push((data as TokenMessageIn).token);
        this.accessToken = (data as TokenMessageIn).token;
        while (this.tokenResolvers.length > 0) {
          this.tokenResolvers.pop()(this.accessToken);
        }
        break;
      }
      case MSG_CAMPAIGN_INFO: {
        this.campaign.update(data.campaign);
        break;
      }
      case MSG_THEME: {
        this.messageQueue[msgType].push((data as ThemeMessageIn).theme);
        break;
      }
      // case MSG_CAMPAIGN_URL: {  // not sent anymore, included with campaign
      //   this.messageQueue[msgType].push((data as CampaignUrlMessageIn).url);
      //   break;
      // }
      case MSG_MEDIA_PICKED: {
        const fieldId = (data as MediaPickedMessageIn).fieldId || "";
        this.messageQueue[msgType].push({
          url: (data as MediaPickedMessageIn).url,
          fieldId: fieldId,
        });
        const resolver = this.mediaResolvers[fieldId];
        if (resolver) {
          resolver(data);
        }
        delete this.mediaResolvers[fieldId];
        break;
      }
      default: {
        break;
      }
    }

    this.pushFromQueue(msgType);
  }
}
